/**
 * The block kit's animated `<details>`, ported into Quil.
 *
 * SOURCE: `app-platform/blocks/cupertino-kit.html`, `smoothDetails`, used by modules 10 (the
 * expanding feature grid) and 13 (the accordion). **The kit is the source and is read-only from
 * here.** A bug in this logic is fixed in the kit first and ported outward — the same rule the
 * rail port carries, and for the same reason.
 *
 * This is the estate's first port of this particular engine; the rail went first, into Contently.
 *
 * ── Why it is script-driven at all ───────────────────────────────────────────────────────────────
 * A native `<details>` has no height to animate: it is shut, then it is open, and everything below
 * it jumps by the full height in one frame. The fade masked that on the way in and did nothing at
 * all on the way out. So the open and the close are driven here while the element stays a real
 * `<details>` — keyboard operable, correct to a screen reader, and still fine with JS off.
 *
 * ── The four things that took rounds to find, all carried across ─────────────────────────────────
 *   · THE PADDING TRAVELS WITH THE HEIGHT. `box-sizing: border-box` clamps a height to at least its
 *     own padding, so animating height alone to zero stops dead at the padding and holds there for
 *     the rest of the run, then loses that last slab in one frame. Measured in the kit: 76px to
 *     26px in 90ms, nothing for 115ms, then 26px gone instantly. That pause-then-snap is what read
 *     as jitter, and it is why two earlier fixes changed nothing.
 *   · THE CLOSE HOLDS AT ZERO (`fill: 'forwards'`). Without it the animation lets go the moment it
 *     ends, the height springs back to its natural value for the one frame before `open` flips
 *     false, and the panel flashes to full size on its way out. The open needs the opposite — it
 *     must let go, so the panel returns to `height: auto` and can reflow when text rewraps.
 *   · COMPLETION CANNOT HANG ON `finish` ALONE. That event rides the rendering pipeline, so a page
 *     that is not being painted (a background tab) may never deliver it, and a closing panel would
 *     stay open at zero height: invisible, still present, still in the tab order. Whichever of the
 *     event or a timer arrives first completes the close.
 *   · MOTION OFF IS THE NATIVE JUMP, not a slower animation. Instant is the correct reduced-motion
 *     answer here.
 *
 * ── What Quil adds ───────────────────────────────────────────────────────────────────────────────
 * Teardown, exactly as the rail port did. The kit runs once on a static page and never unbinds; a
 * component mounts and unmounts, and StrictMode does it twice, so the listener goes through an
 * AbortController, the guard timer is cleared, and a run still in flight is cancelled and its
 * inline styles reset — otherwise an unmount mid-close leaves a panel pinned at zero height.
 */

import { motionReduced } from './usePrefersReducedMotion'

/** Height + padding, the three properties that have to travel together. */
type Frame = Record<'height' | 'paddingTop' | 'paddingBottom', string>

export interface DetailsOptions {
  /** The body element to animate. Defaults to the first element child after `<summary>`. */
  body?: HTMLElement | null
}

/**
 * Wire one `<details>` so it opens and closes instead of snapping. Returns a teardown function.
 */
export function initDetails(el: HTMLDetailsElement, opts: DetailsOptions = {}): () => void {
  const summary = el.querySelector('summary')
  const body = opts.body ?? (summary?.nextElementSibling as HTMLElement | null)
  // `Element.prototype.animate` is the whole mechanism; without it the native behaviour is the
  // only behaviour, which still opens and closes correctly.
  if (!summary || !body || typeof el.animate !== 'function') return () => {}

  const ac = new AbortController()
  // motionReduced(), not matchMedia: Quil has two sources for this preference, the OS query and
  // its own in-app Settings toggle, and reading only the first ignores the switch the user flipped.
  const motionOn = () => !motionReduced()

  let anim: Animation | null = null
  let guard: number | null = null

  const reset = () => {
    body.style.overflow = ''
    body.style.height = ''
    body.style.paddingTop = ''
    body.style.paddingBottom = ''
  }

  summary.addEventListener(
    'click',
    (e) => {
      if (!motionOn()) return // the native jump: instant, which is the right reduced-motion answer
      e.preventDefault()

      if (guard !== null) {
        window.clearTimeout(guard)
        guard = null
      }
      if (anim) {
        anim.cancel()
        anim = null
      }

      const opening = !el.open
      if (opening) el.open = true

      const cs = getComputedStyle(body)
      const shut: Frame = { height: '0px', paddingTop: '0px', paddingBottom: '0px' }
      const full: Frame = {
        height: `${body.offsetHeight}px`,
        paddingTop: cs.paddingTop,
        paddingBottom: cs.paddingBottom,
      }

      const dur = opening ? 260 : 200
      body.style.overflow = 'hidden'

      const running = body.animate(opening ? [shut, full] : [full, shut], {
        duration: dur,
        easing: opening ? 'cubic-bezier(0.22,0.85,0.28,1)' : 'cubic-bezier(0.4,0,0.2,1)',
        fill: opening ? 'none' : 'forwards',
      })
      anim = running

      let settled = false
      const complete = () => {
        if (settled) return
        settled = true
        if (guard !== null) {
          window.clearTimeout(guard)
          guard = null
        }
        if (!opening) {
          el.open = false
          running.cancel() // shut already, so dropping the fill cannot be seen
        }
        reset()
        if (anim === running) anim = null
      }

      running.onfinish = complete
      running.oncancel = reset // a cancelled run is a re-click mid-animation; the next takes over

      // Generous, because this is only ever a fallback: a WAAPI animation does not start until the
      // frame after it is created, so a tight margin risks the timer cutting a run that is still on
      // screen — which would itself look like a jump. Landing late costs nothing.
      guard = window.setTimeout(complete, dur + 250)
    },
    { signal: ac.signal },
  )

  return () => {
    ac.abort()
    if (guard !== null) window.clearTimeout(guard)
    // An unmount mid-close would otherwise leave the panel pinned at zero by its forwards fill.
    anim?.cancel()
    anim = null
    reset()
  }
}
